import axios from 'axios'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { SetLandingDocument, SetLink, jpSetLandingsRepository } from '../repositories/jp_set_landings_repository'
import { useIndexabilityReport } from './indexability'

import '../stylesheets/SetLanding.scss'

type Load = Readonly<{
  status: 'loading' | 'ready' | 'missing' | 'error'
  doc?: SetLandingDocument
}>

const isNotFound = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 404
}

const useLanding = (loader: () => Promise<SetLandingDocument>, key: string): Load => {
  const [load, setLoad] = useState<Load>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    setLoad({ status: 'loading' })
    loader()
      .then((doc) => {
        if (cancelled) return
        document.title = doc.title
        setLoad({ status: 'ready', doc })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setLoad({ status: isNotFound(error) ? 'missing' : 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps

  return load
}

const Crumbs = ({ doc }: { doc: SetLandingDocument }) => (
  <nav aria-label="パンくず" className="jp-set-crumbs">
    { doc.breadcrumbs.map((crumb, index) => {
      const last = index === doc.breadcrumbs.length - 1
      if (!crumb.path || last) return <span key={ crumb.name }>{ crumb.name }</span>
      return <Link key={ crumb.path } to={ crumb.path }>{ crumb.name }</Link>
    }) }
  </nav>
)

const LinkList = ({ links }: { links: SetLink[] }) => (
  <ul>
    { links.map((link) => (
      <li key={ link.path }>
        <Link to={ link.path }>{ link.name }</Link>
        { link.snippet ? <span className="snippet">{ link.snippet }</span> : null }
      </li>
    )) }
  </ul>
)

const LandingBody = ({ load }: { load: Load }) => {
  useIndexabilityReport(
    load.status === 'missing'
      ? { entity: 'missing' }
      : load.status === 'ready'
        ? { entity: 'present' }
        : { entity: 'loading' }
  )
  if (load.status === 'loading') return <p>読み込み中…</p>
  if (load.status === 'error') return <p>読み込めませんでした。しばらくしてからもう一度お試しください。</p>
  if (load.status === 'missing' || !load.doc) return <p>このページは見つかりません。</p>

  const doc = load.doc
  return (
    <article className="jp-set-landing">
      <Crumbs doc={ doc } />
      <h1>{ doc.h1 }</h1>
      <p className="set-intro">{ doc.intro }</p>
      { doc.listingsPath && doc.listingsLabel
        ? <p><Link to={ doc.listingsPath }>{ doc.listingsLabel }</Link></p>
        : null }
      { doc.eraPath && doc.eraName
        ? <p><Link to={ doc.eraPath }>{ doc.eraName }のセット一覧</Link></p>
        : null }
      { doc.eras.length > 0
        ? <>
          <h2>時代</h2>
          <LinkList links={ doc.eras } />
        </>
        : null }
      { doc.sets.length > 0
        ? <>
          <h2>セット</h2>
          <LinkList links={ doc.sets } />
        </>
        : null }
      { doc.cards.length > 0
        ? <>
          <h2>収録カード</h2>
          <ol className="set-cards">
            { doc.cards.map((card) => (
              <li key={ card.path }>
                <Link to={ card.path }>{ card.name }</Link>
                { card.snippet ? <span className="snippet">{ card.snippet }</span> : null }
              </li>
            )) }
          </ol>
        </>
        : null }
      { doc.siblings.length > 0
        ? <>
          <h2>同じ時代のセット</h2>
          <LinkList links={ doc.siblings } />
        </>
        : null }
    </article>
  )
}

const JpSetIndex = (): JSX.Element => {
  const load = useLanding(() => jpSetLandingsRepository.index(), 'index')
  return <LandingBody load={ load } />
}

const JpSetEra = (): JSX.Element => {
  const { slug = '' } = useParams()
  const load = useLanding(() => jpSetLandingsRepository.era(slug), slug)
  return <LandingBody load={ load } />
}

const JpSetLanding = (): JSX.Element => {
  const { slug = '' } = useParams()
  const load = useLanding(() => jpSetLandingsRepository.get(slug), slug)
  return <LandingBody load={ load } />
}

const JpSetHubLinks = (): JSX.Element | null => {
  const [doc, setDoc] = useState<SetLandingDocument | null>(null)

  useEffect(() => {
    let cancelled = false
    jpSetLandingsRepository.index()
      .then((next) => {
        if (!cancelled) setDoc(next)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  if (!doc) return null

  const highlighted = doc.sets.slice(0, 24)

  return (
    <nav className="jp-set-hub" aria-label="拡張パック">
      <h2>拡張パック</h2>
      <p className="set-intro">{ doc.intro }</p>
      <p><Link to="/sets">すべての拡張パック</Link></p>
      <LinkList links={ doc.eras } />
      <LinkList links={ highlighted } />
    </nav>
  )
}

export { JpSetEra, JpSetHubLinks, JpSetIndex, JpSetLanding }
